<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-two" aria-hidden="true"></div>

    <StandardNavBar />

    <main>
      <!-- ============ HERO ============ -->
      <section class="hero shell">
        <div class="hero-copy">
          <p class="eyebrow">
            <span class="eyebrow-line"></span>
            Established since {{ overview.established }} · Singapore
          </p>

          <h1 class="display-1">
            Building maintenance,
            <span>civil works and rope access.</span>
          </h1>

          <p class="hero-intro">{{ overview.intro }}</p>
          <p class="hero-scope">{{ overview.scope }}</p>

          <div class="hero-actions">
            <RouterLink class="btn btn-primary btn-lg" to="/contact">
              Enquire About a Project
              <ArrowRight />
            </RouterLink>
            <RouterLink class="btn btn-ghost btn-lg" to="/services">View Our Services</RouterLink>
          </div>

          <dl class="hero-facts">
            <div v-for="fact in heroFacts" :key="fact.label">
              <dt>{{ fact.label }}</dt>
              <dd>{{ fact.value }}</dd>
            </div>
          </dl>
        </div>

        <div class="hero-visual">
          <figure class="hero-photo hero-photo-main">
            <img :src="homeHero.lead.src" :alt="homeHero.lead.alt" />
            <figcaption>
              <span class="tag tag-light">Rope access division</span>
              <strong>{{ homeHero.lead.caption }}</strong>
              <span class="hero-photo-location">{{ homeHero.lead.location }}</span>
            </figcaption>
          </figure>

          <div class="hero-photo-stack">
            <figure v-for="item in homeHero.support" :key="item.id" class="hero-photo">
              <img :src="item.src" :alt="item.alt" />
              <figcaption>
                <strong>{{ item.service }}</strong>
                <span class="hero-photo-location">{{ item.location || "Building and civil division" }}</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <!-- ============ BCA REGISTRATIONS ============ -->
      <section class="credentials shell" aria-label="BCA registrations">
        <div class="credentials-intro">
          <BadgeCheck />
          <div>
            <strong>The company is registered with BCA under:</strong>
            <p>Building and Construction Authority of Singapore registered workheads.</p>
          </div>
        </div>

        <div class="credentials-grid">
          <div v-for="item in bcaRegistrations" :key="item.code" class="credential">
            <strong>{{ item.code }}</strong>
            <span>{{ item.title }}</span>
          </div>
        </div>

        <CertificationMarks class="credentials-certs" label="Certifications" size="sm" />
      </section>

      <!-- ============ CLIENT LOGOS ============ -->
      <div class="logo-band shell">
        <p class="logo-band-label">Buildings and developments our teams have worked on</p>

        <div class="logo-marquee" aria-label="Project and client logos">
          <div class="logo-track">
            <div
              v-for="(clientLogo, index) in duplicatedClientLogos"
              :key="`${clientLogo.name}-${index}`"
              class="logo-cell"
            >
              <img :src="clientLogo.logo" :alt="clientLogo.name" />
            </div>
          </div>
        </div>
      </div>

      <!-- ============ HOW WE WORK ============ -->
      <section class="statement">
        <div class="statement-rule" aria-hidden="true"></div>
        <div class="statement-inner shell">
          <h2 class="display-2">
            Built on hands-on experience,
            <span>reliable workmanship and close coordination.</span>
          </h2>

          <div class="statement-copy">
            <p>{{ overview.experience }}</p>
            <p>{{ overview.approach }}</p>
            <RouterLink class="text-link" to="/about">
              Read our story
              <ArrowRight />
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============ SERVICES PREVIEW ============ -->
      <section class="section-pad services-preview shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Services
            </p>
            <h2 class="display-2">
              Core services
              <span>for buildings in daily operation.</span>
            </h2>
          </div>

          <p class="lead">{{ services.intro }}</p>
        </div>

        <div class="service-grid">
          <article
            v-for="(service, index) in services.core"
            :key="service"
            class="card card-hover service-card"
          >
            <div class="icon-tile">
              <component :is="coreServiceIcons[index]" />
            </div>
            <span class="service-index">{{ String(index + 1).padStart(2, "0") }}</span>
            <h3>{{ service }}</h3>
          </article>
        </div>

        <div class="card other-preview">
          <div>
            <p class="subsection-label">Other services</p>
            <p>
              Project management support, building and civil works, installation and dismantling,
              minor repairs, window and skylight cleaning, sealant works, cladding and glazing panel
              replacement, Periodic Façade Inspection (PFI) and signage works.
            </p>
          </div>

          <RouterLink class="btn btn-ghost" to="/services">
            See all services
            <ArrowRight />
          </RouterLink>
        </div>
      </section>

      <!-- ============ DIVISIONS PREVIEW ============ -->
      <section class="divisions-preview">
        <div class="shell">
          <div class="section-head">
            <div>
              <p class="eyebrow">
                <span class="eyebrow-line"></span>
                Business divisions
              </p>
              <h2 class="display-2">
                Two divisions,
                <span>one accountable team.</span>
              </h2>
            </div>

            <p class="lead">
              Depending on the scope, work is delivered by our Building and Civil Division, our Rope
              Access Services Division, or both working together on the same project.
            </p>
          </div>

          <div class="division-grid">
            <RouterLink class="card card-hover division-card" to="/divisions">
              <div class="division-media">
                <img
                  :src="divisionImages.buildingCivil.src"
                  :alt="divisionImages.buildingCivil.alt"
                  loading="lazy"
                />
                <span class="division-number">{{ buildingCivilDivision.number }}</span>
              </div>

              <div class="division-body">
                <div class="division-title">
                  <Building2 />
                  <h3>{{ buildingCivilDivision.name }}</h3>
                </div>
                <p>{{ buildingCivilDivision.paragraphs[0] }}</p>
                <span class="text-link">
                  About this division
                  <ArrowRight />
                </span>
              </div>
            </RouterLink>

            <RouterLink class="card card-hover division-card" to="/divisions#rope-access">
              <div class="division-media">
                <img
                  :src="divisionImages.ropeAccess.src"
                  :alt="divisionImages.ropeAccess.alt"
                  loading="lazy"
                />
                <span class="division-number">{{ ropeAccessDivision.number }}</span>
              </div>

              <div class="division-body">
                <div class="division-title">
                  <Cable />
                  <h3>{{ ropeAccessDivision.name }}</h3>
                </div>
                <p>{{ ropeAccessDivision.intro }}</p>
                <span class="text-link">
                  About this division
                  <ArrowRight />
                </span>
              </div>
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============ ROPE ACCESS CAPABILITY (DARK) ============ -->
      <section class="capability">
        <div class="capability-texture" aria-hidden="true"></div>

        <div class="shell capability-inner">
          <div class="capability-head">
            <p class="eyebrow eyebrow-dark">
              <span class="eyebrow-line"></span>
              Rope access capability
            </p>
            <h2 class="display-2">
              A flexible alternative to
              <span>scaffolding and staging.</span>
            </h2>
            <p class="capability-lead">{{ ropeAccessCapability.intro }}</p>
          </div>

          <div class="capability-body">
            <div>
              <p class="dark-label">{{ ropeAccessCapability.advantagesLead }}</p>

              <div class="advantage-list">
                <article
                  v-for="(advantage, index) in ropeAccessCapability.advantages"
                  :key="advantage"
                  class="advantage"
                >
                  <component :is="advantageIcons[index]" />
                  <strong>{{ advantage }}</strong>
                </article>
              </div>
            </div>

            <div class="capability-note">
              <p class="dark-label">How the work is controlled</p>
              <p>{{ ropeAccessCapability.practice }}</p>
              <p>{{ ropeAccessCapability.systems }}</p>

              <RouterLink class="btn btn-primary" to="/divisions#rope-access-capability">
                Rope access capability in detail
                <ArrowRight />
              </RouterLink>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ SELECTED WORK ============ -->
      <section class="section-pad work-preview shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Our work
            </p>
            <h2 class="display-2">
              Photographs from
              <span>live sites, not stock.</span>
            </h2>
          </div>

          <p class="lead">
            Cleaning, painting, sealant and cladding works carried out on rope, alongside repair,
            waterproofing and reinstatement works on the ground.
          </p>
        </div>

        <ul class="work-strip">
          <li v-for="item in featuredWork" :key="item.id" class="work-tile">
            <img :src="item.src" :alt="item.alt" loading="lazy" />
            <div class="work-tile-meta">
              <strong>{{ item.service }}</strong>
              <span v-if="item.location">{{ item.location }}</span>
            </div>
          </li>
        </ul>

        <div class="rule-note work-note">
          <span></span>
          <div>
            <p>
              Every photo on this site was taken on a Blue Speed job, on buildings that stayed in use
              while we worked.
            </p>
            <RouterLink class="text-link" to="/our-work">
              See all project photographs
              <ArrowRight />
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============ QUALITY PREVIEW ============ -->
      <section class="section-pad quality-preview shell">
        <div class="section-head">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Quality, safety and training
            </p>
            <h2 class="display-2">
              Good service is not only
              <span>about completing the work.</span>
            </h2>
          </div>

          <p class="lead">{{ qualitySafety.belief }}</p>
        </div>

        <div class="quality-grid">
          <article
            v-for="(pillar, index) in qualitySafety.pillars"
            :key="pillar.key"
            class="card quality-card"
          >
            <div class="icon-tile">
              <component :is="qualityIcons[index]" />
            </div>
            <h3>{{ pillar.title }}</h3>
            <p>{{ pillar.body }}</p>
          </article>
        </div>

        <div class="rule-note quality-goal">
          <span></span>
          <div>
            <p>{{ qualitySafety.goal }}</p>
            <RouterLink class="text-link" to="/quality-safety">
              How we manage quality, safety and training
              <ArrowRight />
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============ FINAL CTA ============ -->
      <section class="final-cta">
        <div class="final-cta-rule" aria-hidden="true"></div>
        <div class="final-cta-inner shell">
          <div>
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              Planning an upcoming project
            </p>
            <h2 class="display-2">
              Let's discuss the scope,
              <span>plan the work and get it moving.</span>
            </h2>
          </div>

          <div class="final-cta-action">
            <p>
              Share your project type, required works, location and timeline. We will review the scope
              and advise on the next practical step.
            </p>
            <RouterLink class="btn btn-primary btn-lg" to="/contact">
              Contact Blue Speed
              <ArrowRight />
            </RouterLink>
            <a class="btn btn-ghost btn-lg" :href="whatsappUrl" target="_blank" rel="noopener noreferrer">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import {
  ArrowRight,
  BadgeCheck,
  Brush,
  Building2,
  Cable,
  ClipboardCheck,
  Droplets,
  GraduationCap,
  HardHat,
  Layers,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-vue-next";

import CertificationMarks from "@/components/CertificationMarks.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import { whatsappLink } from "@/config/company";
import { divisionImages, featuredWork, homeHero } from "@/content/gallery";
import {
  bcaRegistrations,
  buildingCivilDivision,
  overview,
  qualitySafety,
  ropeAccessCapability,
  ropeAccessDivision,
  services,
} from "@/content/company-content";

export default {
  name: "HomePage",

  components: {
    ArrowRight,
    BadgeCheck,
    Building2,
    Cable,
    CertificationMarks,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      overview,
      bcaRegistrations,
      services,
      buildingCivilDivision,
      ropeAccessDivision,
      ropeAccessCapability,
      qualitySafety,

      homeHero,
      divisionImages,
      featuredWork,

      whatsappUrl: whatsappLink(),

      coreServiceIcons: [Wrench, HardHat, Droplets, Brush],
      advantageIcons: [Building2, Layers, Sparkles],
      qualityIcons: [ClipboardCheck, ShieldCheck, GraduationCap],

      heroFacts: [
        { label: "Established", value: "Since 1996" },
        { label: "BCA workheads", value: "CW01 · CR09 · FM02" },
        { label: "Divisions", value: "Building & Civil · Rope Access" },
      ],

      clientLogos: [
        { name: "Suntec City", logo: require("@/assets/sunteclogo.jpg") },
        { name: "Resorts World Sentosa", logo: require("@/assets/rws_logo.png") },
        { name: "Marina Bay Sands", logo: require("@/assets/mbs_logo.png") },
        { name: "Marina Bay Financial Centre", logo: require("@/assets/mbfc_logo.png") },
        { name: "Mount Elizabeth Hospital", logo: require("@/assets/mountE_logo.png") },
        { name: "Singapore Expo", logo: require("@/assets/singapore_expo_logo.png") },
        { name: "Esplanade", logo: require("@/assets/esplanade_logo.png") },
        { name: "Marina Square", logo: require("@/assets/marinasquare_logo.jpg") },
      ],
    };
  },

  computed: {
    duplicatedClientLogos() {
      return [...this.clientLogos, ...this.clientLogos];
    },
  },
};
</script>

<style scoped>
/* ---------- hero ---------- */

.hero {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 0.86fr) minmax(0, 1.14fr);
  align-items: center;
  gap: 56px;
  padding: 160px 0 88px;
}

.hero-intro {
  max-width: 620px;
  margin: 28px 0 0;
  color: #2e5278;
  font-size: 17px;
  line-height: 1.72;
}

.hero-scope {
  max-width: 620px;
  margin: 16px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;
}

.hero-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  max-width: 640px;
  margin: 42px 0 0;
  border-top: 1px solid #ccdce9;
}

.hero-facts > div {
  padding: 20px 18px 0 0;
}

.hero-facts > div + div {
  padding-left: 20px;
  border-left: 1px solid #ccdce9;
}

.hero-facts dt {
  color: var(--muted-light);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-facts dd {
  margin: 8px 0 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}

.hero-visual {
  display: grid;
  grid-template-columns: minmax(0, 1.06fr) minmax(0, 0.94fr);
  gap: 14px;
}

.hero-photo {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--navy);
  box-shadow: var(--shadow-card);
}

.hero-photo img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-photo::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(6, 18, 38, 0.32) 0%,
    rgba(6, 18, 38, 0.22) 38%,
    rgba(6, 18, 38, 0.86) 100%
  );
}

.hero-photo figcaption {
  position: relative;
  z-index: 2;
  padding: 22px;
}

.hero-photo figcaption strong {
  display: block;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 15px;
  line-height: 1.35;
  letter-spacing: -0.02em;
}

.hero-photo-main {
  min-height: 520px;
}

.hero-photo-main figcaption strong {
  margin-top: 14px;
  font-size: 21px;
  line-height: 1.24;
}

.tag-light {
  border-color: rgba(255, 255, 255, 0.36);
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
}

.hero-photo-location {
  display: block;
  margin-top: 8px;
  color: #a9c9ec;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.hero-photo-stack {
  display: grid;
  gap: 14px;
}

.hero-photo-stack .hero-photo {
  min-height: 253px;
}

/* ---------- selected work ---------- */

.work-preview {
  padding-top: 0;
}

.work-strip {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin: 52px 0 0;
  padding: 0;
  list-style: none;
}

.work-tile {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--navy);
  box-shadow: var(--shadow-card);
}

.work-tile img {
  display: block;
  width: 100%;
  height: 260px;
  object-fit: cover;
  transition: transform 600ms ease;
}

.work-tile:hover img {
  transform: scale(1.05);
}

.work-tile::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(6, 18, 38, 0) 44%, rgba(6, 18, 38, 0.84) 100%);
}

.work-tile-meta {
  position: absolute;
  z-index: 2;
  right: 14px;
  bottom: 14px;
  left: 14px;
}

.work-tile-meta strong {
  display: block;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 13px;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.work-tile-meta span {
  display: block;
  margin-top: 6px;
  color: #a9c9ec;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}

.work-note {
  max-width: 880px;
  margin-top: 40px;
}

.work-note p {
  margin: 0 0 10px;
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.8;
}

/* ---------- credentials ---------- */

.credentials {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(280px, 0.66fr) minmax(0, 1.34fr);
  align-items: center;
  gap: 32px;
  margin-bottom: 26px;
  padding: 28px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.credentials-intro {
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 16px;
  align-items: start;
}

.credentials-intro svg {
  width: 32px;
  height: 32px;
  color: var(--blue);
}

.credentials-intro strong {
  display: block;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 15px;
  line-height: 1.4;
}

.credentials-intro p {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.65;
}

.credentials-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.credential {
  padding: 18px;
  border: 1px solid #dbe7f1;
  background: var(--surface-tint);
}

.credential strong {
  display: block;
  color: var(--blue);
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 800;
}

.credential span {
  display: block;
  margin-top: 8px;
  color: #4e6d8c;
  font-size: 11px;
  line-height: 1.55;
}

.credentials-certs {
  grid-column: 1 / -1;
  padding-top: 24px;
  border-top: 1px solid var(--line);
}

/* ---------- logo band ---------- */

.logo-band {
  position: relative;
  z-index: 2;
  padding: 28px 0;
  border: 1px solid #d7e0ea;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 50px rgba(32, 76, 126, 0.06);
  overflow: hidden;
}

.logo-band-label {
  margin: 0 24px 22px;
  color: #6e7f92;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-align: center;
}

.logo-marquee {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.logo-marquee::before,
.logo-marquee::after {
  content: "";
  position: absolute;
  top: 0;
  z-index: 3;
  width: 110px;
  height: 100%;
  pointer-events: none;
}

.logo-marquee::before {
  left: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.98), transparent);
}

.logo-marquee::after {
  right: 0;
  background: linear-gradient(270deg, rgba(255, 255, 255, 0.98), transparent);
}

.logo-track {
  display: flex;
  width: max-content;
  gap: 14px;
  padding: 0 14px;
  animation: logoScroll 34s linear infinite;
}

.logo-marquee:hover .logo-track {
  animation-play-state: paused;
}

@keyframes logoScroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.logo-cell {
  flex: 0 0 200px;
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  border: 1px solid #dbe7f1;
  background: #ffffff;
}

.logo-cell img {
  max-width: 144px;
  max-height: 50px;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.7;
  transition:
    filter 180ms ease,
    opacity 180ms ease;
}

.logo-cell:hover img {
  filter: grayscale(0);
  opacity: 1;
}

/* ---------- statement ---------- */

.statement {
  position: relative;
  z-index: 2;
  margin-top: 88px;
  padding: 104px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #eef4fb;
}

.statement-rule {
  position: absolute;
  top: -1px;
  left: 0;
  width: 28%;
  height: 2px;
  background: var(--blue);
}

.statement-inner {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(340px, 0.85fr);
  gap: 64px;
  align-items: start;
}

.statement-copy p {
  margin: 0 0 16px;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.85;
}

.text-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: var(--blue);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

.text-link svg {
  width: 15px;
  height: 15px;
  transition: transform 180ms ease;
}

.text-link:hover svg {
  transform: translateX(3px);
}

/* ---------- services preview ---------- */

.service-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 56px;
}

.service-card {
  position: relative;
  min-height: 210px;
  display: flex;
  flex-direction: column;
  padding: 26px;
}

.service-index {
  position: absolute;
  top: 26px;
  right: 24px;
  color: #b6c9db;
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.service-card h3 {
  margin: auto 0 0;
  padding-top: 26px;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 20px;
  line-height: 1.28;
  letter-spacing: -0.03em;
}

.other-preview {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 36px;
  margin-top: 14px;
  padding: 30px;
  background: linear-gradient(135deg, #f7fbff, #ffffff);
}

.other-preview p:last-of-type {
  max-width: 820px;
  margin: 14px 0 0;
  color: var(--muted);
  font-size: 13.5px;
  line-height: 1.8;
}

/* ---------- divisions preview ---------- */

.divisions-preview {
  position: relative;
  z-index: 2;
  padding: 124px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #f5f9fd;
}

.division-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 56px;
}

.division-card {
  display: flex;
  flex-direction: column;
  text-decoration: none;
}

.division-media {
  position: relative;
  height: 260px;
  overflow: hidden;
}

.division-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 500ms ease;
}

.division-card:hover .division-media img {
  transform: scale(1.04);
}

.division-media::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(6, 18, 38, 0.34), rgba(6, 18, 38, 0.12));
}

.division-number {
  position: absolute;
  z-index: 2;
  left: 24px;
  bottom: 18px;
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 38px;
  font-weight: 800;
  letter-spacing: -0.04em;
  opacity: 0.86;
}

.division-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 28px 30px 30px;
}

.division-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.division-title svg {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  color: var(--blue);
}

.division-title h3 {
  margin: 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.035em;
}

.division-body > p {
  margin: 18px 0 24px;
  color: var(--muted);
  font-size: 13.5px;
  line-height: 1.8;
}

.division-body .text-link {
  margin-top: auto;
}

/* ---------- capability (dark) ---------- */

.capability {
  position: relative;
  z-index: 2;
  overflow: hidden;
  padding: 124px 0;
  background: var(--navy);
  color: #e6eefa;
}

.capability-texture {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(circle at 78% 22%, black, transparent 62%);
}

.capability-inner {
  position: relative;
  z-index: 2;
}

.capability-head {
  max-width: 880px;
}

.eyebrow-dark {
  color: #7fa8d6;
}

.eyebrow-dark .eyebrow-line {
  background: var(--blue-light);
}

.capability :deep(.display-2) {
  color: #ffffff;
}

.capability :deep(.display-2 span) {
  color: #7794b5;
}

.capability-lead {
  max-width: 760px;
  margin: 26px 0 0;
  color: #a8c0da;
  font-size: 15px;
  line-height: 1.8;
}

.capability-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 0.8fr);
  gap: 18px;
  margin-top: 56px;
}

.dark-label {
  margin: 0 0 18px;
  color: #7fa8d6;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.6;
}

.advantage-list {
  display: grid;
  gap: 12px;
}

.advantage {
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  transition:
    border-color 200ms ease,
    background 200ms ease;
}

.advantage:hover {
  border-color: rgba(76, 155, 240, 0.45);
  background: rgba(76, 155, 240, 0.07);
}

.advantage svg {
  width: 22px;
  height: 22px;
  color: var(--blue-light);
}

.advantage strong {
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 18px;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.capability-note {
  display: flex;
  flex-direction: column;
  padding: 30px;
  border: 1px solid rgba(76, 155, 240, 0.3);
  background: linear-gradient(160deg, rgba(76, 155, 240, 0.12), rgba(255, 255, 255, 0.02));
}

.capability-note p:not(.dark-label) {
  margin: 0 0 16px;
  color: #b7cde5;
  font-size: 13.5px;
  line-height: 1.8;
}

.capability-note .btn {
  margin-top: auto;
}

/* ---------- quality preview ---------- */

.quality-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 56px;
}

.quality-card {
  padding: 30px;
}

.quality-card h3 {
  margin: 24px 0 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 22px;
  line-height: 1.22;
  letter-spacing: -0.035em;
}

.quality-card p {
  margin: 14px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.8;
}

.quality-goal {
  max-width: 880px;
  margin-top: 44px;
}

.quality-goal p {
  margin: 0 0 10px;
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.8;
}

/* ---------- final cta ---------- */

.final-cta {
  position: relative;
  z-index: 2;
  overflow: hidden;
  padding: 108px 0;
  border-top: 1px solid rgba(11, 37, 69, 0.07);
  background: #eaf2fb;
}

.final-cta-rule {
  position: absolute;
  top: 0;
  left: 0;
  width: 42%;
  height: 2px;
  background: var(--blue);
}

.final-cta-inner {
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 72px;
  align-items: end;
}

.final-cta-action p {
  margin: 0 0 22px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.8;
}

.final-cta-action .btn {
  width: 100%;
}

.final-cta-action .btn-ghost {
  margin-top: 10px;
}

/* ---------- responsive ---------- */

@media (max-width: 1340px) {
  .hero {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-top: 146px;
  }

  .hero-copy {
    max-width: 860px;
  }
}

@media (max-width: 1280px) {
  .credentials,
  .capability-body,
  .statement-inner {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}

@media (max-width: 1080px) {
  .service-grid,
  .quality-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .work-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .division-grid,
  .other-preview,
  .final-cta-inner {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .final-cta-inner {
    gap: 36px;
  }

  .final-cta-action {
    max-width: 520px;
  }

  .logo-cell {
    flex-basis: 176px;
  }
}

@media (max-width: 760px) {
  .hero {
    padding: 116px 0 68px;
  }

  .hero-visual,
  .hero-facts,
  .credentials-grid,
  .service-grid,
  .quality-grid {
    grid-template-columns: 1fr;
  }

  .hero-facts > div {
    padding: 16px 0;
    border-bottom: 1px solid #ccdce9;
  }

  .hero-facts > div + div {
    padding-left: 0;
    border-left: 0;
  }

  .hero-actions .btn {
    width: 100%;
  }

  .hero-photo-main,
  .hero-photo-stack .hero-photo {
    min-height: 290px;
  }

  .work-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .work-tile img {
    height: 200px;
  }

  .credentials,
  .other-preview {
    padding: 24px;
  }

  .statement {
    margin-top: 68px;
    padding: 80px 0;
  }

  .divisions-preview,
  .capability {
    padding: 80px 0;
  }

  .division-media {
    height: 210px;
  }

  .division-body {
    padding: 24px 22px 26px;
  }

  .logo-marquee::before,
  .logo-marquee::after {
    width: 52px;
  }

  .logo-cell {
    flex-basis: 152px;
    min-height: 80px;
  }

  .logo-cell img {
    max-width: 114px;
  }

  .final-cta {
    padding: 80px 0;
  }
}
</style>
